/**
 * Client-side mirror of server/src/lib/payouts.ts.
 *
 * Used by the create form's live preview. Must produce IDENTICAL output to
 * the server for the same inputs — that's the contract that lets the
 * admin's preview match what players actually receive.
 *
 * If you change one side, change BOTH. (There's a smoke test in the e2e
 * suite that compares previewed vs. closed-tournament amounts.)
 */

export type PayoutSlot = { place: number; pct: number };

export const MIN_PLACES = 1;
export const MAX_PLACES = 10;

export function validatePayoutStructure(slots: PayoutSlot[]): {
  ok: boolean;
  error?: string;
  sum?: number;
} {
  if (!Array.isArray(slots) || slots.length < MIN_PLACES) {
    return { ok: false, error: 'at least one paying place is required' };
  }
  if (slots.length > MAX_PLACES) {
    return { ok: false, error: `at most ${MAX_PLACES} paying places` };
  }
  const places = slots.map((s) => s.place).sort((a, b) => a - b);
  for (let i = 0; i < places.length; i++) {
    if (places[i] !== i + 1) {
      return { ok: false, error: 'places must be contiguous 1..N' };
    }
  }
  for (const s of slots) {
    if (!Number.isFinite(s.pct) || s.pct <= 0) {
      return { ok: false, error: `place ${s.place} must have a positive percent` };
    }
  }
  const sum = slots.reduce((s, p) => s + p.pct, 0);
  if (Math.abs(sum - 100) > 1e-6) {
    return { ok: false, error: `percents must sum to 100 (got ${sum})`, sum };
  }
  return { ok: true, sum };
}

export function splitPotCents(potCents: number, payouts: PayoutSlot[]): number[] {
  if (!Number.isFinite(potCents) || potCents < 0) {
    throw new Error('potCents must be a non-negative finite number');
  }
  const v = validatePayoutStructure(payouts);
  if (!v.ok) throw new Error(v.error);

  const potDollars = Math.floor(potCents / 100);
  if (potDollars === 0) return payouts.map(() => 0);

  const raw = payouts.map((p) => (potDollars * p.pct) / 100);
  const floors = raw.map((r) => Math.floor(r));
  const totalFloor = floors.reduce((s, n) => s + n, 0);
  const remainder = potDollars - totalFloor;

  const fracs = raw.map((r, i) => ({ i, frac: r - Math.floor(r) }));
  fracs.sort((a, b) => b.frac - a.frac || a.i - b.i);

  const dollars = floors.slice();
  for (let k = 0; k < remainder && k < fracs.length; k++) {
    dollars[fracs[k]!.i]! += 1;
  }
  return dollars.map((d) => d * 100);
}

export function computePotCents(entryFeeCents: number, entryCount: number): number {
  if (!Number.isFinite(entryFeeCents) || entryFeeCents < 0) return 0;
  if (!Number.isFinite(entryCount) || entryCount < 0) return 0;
  return Math.floor(entryFeeCents) * Math.floor(entryCount);
}

export function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: dollars % 1 === 0 ? 0 : 2,
  });
}
